import React, { useState, useEffect, useRef, useCallback } from 'react';
import Button from '../BaseComponents/Button';
import NotesTable from './NotesTable';
import FollowOnModal from './FollowOn/FollowOnModal';
import FollowOnAlertsTable from './FollowOn/AlertsTable/FollowOnAlertsTable';
import { createFollowOn, listFollowOn, FollowOnItem, deleteFollowOn, editFollowOn } from '../../api/follow-on';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import { openNotesModal } from '../../store/memberSlice';
import { getMemberNotes, NoteResponse } from '../../api/notes';
import { ListNoteResponseElement } from '../../types/apiGeneratedTypes';
import { getUnprocessedTestResultsCount } from '../../api/testResults';
import { setAlertsCount } from '../../store/alertsSlice';
import { useLocation } from 'react-router-dom';
import './notes.scss';
import ReferralLetterTable from './ReferralLetterTable';
import ReferralLetterDownloadModal from './ReferralLetter/ReferralLetterDownloadModal';
import ReferralLetterViewModal from './ReferralLetter/ReferralLetterViewModal';
import { downloadReferralLetterPdf } from '../../utils/downloadReferralLetterPdf';

type ReferralPdfViewState = {
  url: string | null;
  filename: string;
  isGenerating: boolean;
};

const Notes: React.FC<{ noteTab: 'notes' | 'followOnAlerts' | 'referralLetter' }> = ({ noteTab }) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const [notesList, setNotesList] = useState<NoteResponse[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [sortingLoading, setSortingLoading] = useState<boolean>(false);
  const [sortField, setSortField] = useState<string | undefined>(undefined);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [activeSubTab, setActiveSubTab] = useState<'notes' | 'followOnAlerts' | 'referralLetter' | null>(null);
  const [isFollowOnModalOpen, setIsFollowOnModalOpen] = useState(false);
  const [followOnAlerts, setFollowOnAlerts] = useState<FollowOnItem[]>([]);
  const [followOnToEdit, setFollowOnToEdit] = useState<FollowOnItem | null>(null);
  const followOnLoadedRef = useRef(false);
  const wasNotesModalOpenRef = useRef(false);

  const { isNotesModalOpened, memberData, notesList: reduxNotesList } = useSelector((state: RootState) => state.member);
  const assigned_doctor = JSON.parse(sessionStorage.getItem("assigned_doctor") ?? "false");
  const { admin: { role } } = useSelector((state: RootState) => state.admin);
  const isReadOnly = role === "doctor" && !assigned_doctor;
  const [isReferralDownloadModalOpen, setIsReferralDownloadModalOpen] = useState(false);
  const [referralNoteToDownload, setReferralNoteToDownload] = useState<NoteResponse | null>(null);
  const [referralPdfView, setReferralPdfView] = useState<ReferralPdfViewState | null>(null);

  // Handle location state for sub-tab navigation
  useEffect(() => {
    if (location.state?.activeSubTab) {
      setActiveSubTab(location.state.activeSubTab);
    }

    // Handle follow-on modal opening from alerts
    if (location.state?.openFollowOnModal && location.state?.followOnData) {
      setFollowOnToEdit(location.state.followOnData);
      setIsFollowOnModalOpen(true);
      // Clear the location state to prevent reopening on re-render
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  useEffect(() => {
    // Set active sub-tab based on noteTab prop
    setActiveSubTab(noteTab);
  }, [noteTab]);

  // Reset follow-on loaded flag when member changes
  useEffect(() => {
    followOnLoadedRef.current = false;
  }, [memberData?.user_id]);

  const isReferralLetterTab = activeSubTab === 'referralLetter';

  const filterNotesForActiveTab = useCallback((notes: NoteResponse[]): NoteResponse[] =>
    notes.filter((note) => Boolean(note.is_referral_letter) === isReferralLetterTab),
  [isReferralLetterTab]);

  // Fetch notes from API
   const fetchNotes = useCallback(async () => {
    if (!memberData?.user_id || activeSubTab === 'followOnAlerts') return;

    try {
      setIsLoading(true);
      const response = await getMemberNotes(
        memberData.user_id,
        0, // offset
        50, // limit
        sortField as 'created_at' | 'updated_at' | undefined,
        sortOrder,
        isReferralLetterTab
      );
      setNotesList(response.notes);
    } catch (err) {
      console.error('Error fetching notes:', err);
    } finally {
      setIsLoading(false);
    }
  }, [memberData?.user_id, activeSubTab, sortField, sortOrder, isReferralLetterTab]);

  useEffect(() => {
    if(memberData?.user_id && (activeSubTab === 'notes' || activeSubTab === 'referralLetter')) {
      fetchNotes();
    }
  }, [memberData?.user_id, activeSubTab, fetchNotes]);

  // Load follow-on alerts when member/tab changes
  useEffect(() => {
    const loadFollowOns = async () => {
      if (!memberData?.user_id) return;
      try {
        const resp = await listFollowOn({ member_id: memberData.user_id, limit: 50, offset: 0, sort_field: 'created_at', sort_order: 'desc' });
        setFollowOnAlerts(resp.items || []);
        followOnLoadedRef.current = true;
      } catch (e) {
        console.error('Error fetching follow-on alerts:', e);
        setFollowOnAlerts([]);
        followOnLoadedRef.current = true;
      }
    };

    if (activeSubTab === 'followOnAlerts' && !followOnLoadedRef.current) {
      loadFollowOns();
    }
  }, [memberData?.user_id, activeSubTab]);

  // Convert ListNoteResponseElement to NoteResponse
  const convertToNoteResponse = useCallback((listNote: ListNoteResponseElement): NoteResponse => {
    return {
      id: listNote.id,
      description: listNote.description,
      member_id: memberData?.user_id || '',
      author: listNote.author,
      created_at: listNote.created_at,
      updated_at: listNote.created_at, // Use created_at as fallback since ListNoteResponseElement doesn't have updated_at
      title: listNote.title || '',
      is_private: listNote.is_private || false,
      is_referral_letter: listNote.is_referral_letter || false,
      audio_urls: (listNote as any).audio_urls || [] // Include audio_urls if present
    };
  }, [memberData?.user_id]);

  // Sync with Redux notes list when it changes (from modal operations)
  useEffect(() => {
    if (activeSubTab === 'followOnAlerts' || !reduxNotesList?.length) return;

    setNotesList(filterNotesForActiveTab(reduxNotesList.map(convertToNoteResponse)));
  }, [reduxNotesList, activeSubTab, filterNotesForActiveTab, convertToNoteResponse]);

  const onReferralViewModalClose = () => {
    if (referralPdfView?.url) {
      URL.revokeObjectURL(referralPdfView.url);
    }
    setReferralPdfView(null);
  };

  const onViewNote = async (note: NoteResponse, forceViewOnly: boolean = false) => {
    if (activeSubTab === 'referralLetter' && forceViewOnly) {
      setReferralPdfView({ url: null, filename: '', isGenerating: true });
      try {
        const result = await downloadReferralLetterPdf(note, true, true);
        if (result) {
          setReferralPdfView({
            url: result.url,
            filename: result.filename,
            isGenerating: true,
          });
        }
      } catch (error) {
        console.error('Error generating referral letter PDF:', error);
        onReferralViewModalClose();
      } finally {
        setReferralPdfView((prev) => (prev ? { ...prev, isGenerating: false } : null));
      }
    } else {
      const listNote: ListNoteResponseElement & { audio_urls?: string[]; force_view_only?: boolean } = {
        id: note.id,
        description: note.description,
        created_at: note.created_at,
        author: note.author,
        title: note.title,
        is_private: note.is_private,
        is_referral_letter: note.is_referral_letter,
        audio_urls: note.audio_urls || [],
        force_view_only: forceViewOnly,
      };

      dispatch(openNotesModal({
        intent: forceViewOnly ? 'view' : 'update',
        forceViewOnly,
        isReferralLetter: isReferralLetterTab || note.is_referral_letter === true,
        noteDataToEdit: listNote,
      }));
    }
  };

  const onRequestReferralLetterDownload = (note: NoteResponse) => {
    setReferralNoteToDownload(note);
    setIsReferralDownloadModalOpen(true);
  };

  const onReferralLetterDownloadModalClose = () => {
    setIsReferralDownloadModalOpen(false);
    setReferralNoteToDownload(null);
  };

  const onReferralLetterDownload = async (downloadForGs: boolean) => {
    if (!referralNoteToDownload) return;
    await downloadReferralLetterPdf(referralNoteToDownload, downloadForGs, false);
  };

  const onEditNote = (note: NoteResponse) => {
    onViewNote(note);
  };

  const onDeleteNote = (noteId: number) => {
    // TODO: Implement delete functionality
  };

  const handleSort = async (field: string) => {
    let newOrder: 'asc' | 'desc' = 'asc';
    if (sortField === field && sortOrder === 'asc') {
      newOrder = 'desc';
    }

    setSortField(field);
    setSortOrder(newOrder);

    // Add sorting loader with delay for better UX
    setSortingLoading(true);
    await new Promise(resolve => setTimeout(resolve, 300));
    setSortingLoading(false);
  };

  useEffect(() => {
    if (wasNotesModalOpenRef.current && !isNotesModalOpened) {
      fetchNotes();
    }
    wasNotesModalOpenRef.current = isNotesModalOpened;
  }, [isNotesModalOpened, fetchNotes]);

  const onAddNoteBtnClick = () => {
    dispatch(openNotesModal({
      intent: 'create',
      forceViewOnly: false,
      isReferralLetter: isReferralLetterTab,
      noteDataToEdit: null,
    }));
  };

  const onFollowOnEdit = (alert: any) => {
    setFollowOnToEdit(alert);
    setIsFollowOnModalOpen(true);
  };

  const onFollowOnDelete = async (alertId: number) => {
    try {
      await deleteFollowOn({ follow_on_id: alertId });
      setFollowOnAlerts(prev => prev.filter(alert => alert.id !== alertId));
      setIsFollowOnModalOpen(false);
      setFollowOnToEdit(null);

      try {
        const alerts = await getUnprocessedTestResultsCount();
        dispatch(setAlertsCount(alerts.count));
      } catch (error) {
        console.log('Error refreshing alerts count after follow-on deletion:', error);
      }
    } catch (error) {
      console.error('Error deleting follow-on alert:', error);
    }
  };

  const onFollowOnModalSave = async (data: { followOnDate: string; description: string; mentioned_user_ids: string[] }) => {
    if (!memberData?.user_id) return;

    try {
      if (followOnToEdit) {
        // Update existing follow-on
        const payload = {
          follow_on_id: followOnToEdit.id,
          description: data.description,
          follow_on_date: data.followOnDate,
          mentioned_user_ids: data.mentioned_user_ids,
        };
        await editFollowOn(payload);
        // Refresh the list to get updated data
        const resp = await listFollowOn({ member_id: memberData.user_id, limit: 50, offset: 0, sort_field: 'created_at', sort_order: 'desc' });
        setFollowOnAlerts(resp.items || []);
      } else {
        // Create new follow-on
        const payload = {
          member_id: memberData.user_id,
          description: data.description,
          follow_on_date: data.followOnDate,
          mentioned_user_ids: data.mentioned_user_ids,
        };
        const newFollowOn = await createFollowOn(payload);
        setFollowOnAlerts(prev => [newFollowOn, ...prev]);
      }
      setIsFollowOnModalOpen(false);
      setFollowOnToEdit(null);

      // Refresh alert count after saving follow-on
      try {
        const alerts = await getUnprocessedTestResultsCount();
        dispatch(setAlertsCount(alerts.count));
      } catch (error) {
        console.log('Error refreshing alerts count after follow-on save:', error);
      }
    } catch (error) {
      console.error('Error saving follow-on:', error);
    }
  };

  return (
    <>
      {/* Notes Tab */}
      {activeSubTab === 'notes' && (
        <>
          <div className='search-row-container'>
            <div className='search-and-filter-container sideBarPad2'>
              <div className='count-container'>
                {notesList.length === 1 ? 'Note' : 'Notes'} ({notesList.length})
              </div>
            </div>
            {!isReadOnly && (
              <div className='button-group'>
                <Button
                  isSecondary={true}
                  text='Add note'
                  onClick={onAddNoteBtnClick}
                />
              </div>
            )}
          </div>

          <div className='notes-container'>
            <NotesTable
              notesList={notesList}
              isLoading={isLoading}
              sortingLoading={sortingLoading}
              onViewNote={onViewNote}
              onEditNote={onEditNote}
              onDeleteNote={onDeleteNote}
              onSort={handleSort}
              isReadOnly={isReadOnly}
            />
          </div>
        </>
      )}

      {/* Follow-on Alerts Tab */}
      {activeSubTab === 'followOnAlerts' && (
        <div className='follow-on-alerts-container'>
          <div className='search-row-container sideBarPad2'>
            <div className='search-and-filter-container'>
              <div className='count-container'>
                {`Follow-on (${followOnAlerts?.length || 0})`}
              </div>
            </div>
            {!isReadOnly && (
              <div className='button-group'>
                <Button
                  isSecondary={true}
                  text='Follow-on'
                  onClick={() => {
                    setFollowOnToEdit(null);
                    setIsFollowOnModalOpen(true);
                  }}
                />
              </div>
            )}
          </div>
          <FollowOnAlertsTable
            alerts={followOnAlerts}
            isLoading={false}
            onEdit={onFollowOnEdit}
            onDelete={onFollowOnDelete}
            isReadOnly={isReadOnly}
          />
        </div>
      )}

      {/* Referral Letter Tab */}
      {activeSubTab === 'referralLetter' && (
        <>
          <div className='search-row-container sideBarPad2'>
            <div className='search-and-filter-container'>
              <div className='count-container'>
                {notesList.length === 1 ? 'Referral Letter' : 'Referral Letters'} ({notesList.length})
              </div>
            </div>
            {!isReadOnly && (
              <div className='button-group'>
                <Button
                  isSecondary={true}
                  text='Referral letter'
                  onClick={onAddNoteBtnClick}
                />
              </div>
            )}
          </div>

          <div className='notes-container'>
            <ReferralLetterTable
              notesList={notesList}
              isLoading={isLoading}
              sortingLoading={sortingLoading}
              onViewNote={onViewNote}
              onEditNote={onEditNote}
              onDeleteNote={onDeleteNote}
              onSort={handleSort}
              isReadOnly={isReadOnly}
              onRequestDownload={onRequestReferralLetterDownload}
            />
          </div>
        </>
      )}

      {isFollowOnModalOpen && (
        <FollowOnModal
          isOpen={isFollowOnModalOpen}
          onClose={() => {
            setIsFollowOnModalOpen(false);
            setFollowOnToEdit(null);
          }}
          onSave={onFollowOnModalSave}
          onDelete={followOnToEdit ? () => onFollowOnDelete(followOnToEdit.id) : undefined}
          isEditMode={!!followOnToEdit}
          initialDate={followOnToEdit ? new Date(followOnToEdit.follow_on_date) : null}
          initialDescription={followOnToEdit ? followOnToEdit.description : ''}
        />
      )}

      {isReferralDownloadModalOpen && (
        <ReferralLetterDownloadModal
          isOpen={isReferralDownloadModalOpen}
          onClose={onReferralLetterDownloadModalClose}
          onDownload={onReferralLetterDownload}
        />
      )}

      {referralPdfView && (
        <ReferralLetterViewModal
          isOpen
          onClose={onReferralViewModalClose}
          pdfUrl={referralPdfView.url}
          filename={referralPdfView.filename}
          isGenerating={referralPdfView.isGenerating}
        />
      )}
    </>
  );
};

export default Notes;
